/**
 * Get Accessibility Preferences - Query Hook
 * Following Bulletproof React three-layer pattern for GET operations
 *
 * Layer 1: Pure fetcher function
 * Layer 2: Query options factory
 * Layer 3: React Query hook
 *
 * Fetches user accessibility preferences from user_preferences_accessibility table.
 * Returns defaults if no record exists for the user.
 *
 * IMPORTANT: This API layer handles ONLY pure data fetching.
 * The following logic stays in the wrapper hook (use-accessibility-preferences.ts):
 * - CSS class application (applyAccessibilityClasses)
 * - Debounced updates and optimistic UI updates
 *
 * Cache Invalidation:
 * - Use profileQueryKeys.accessibility(userId) to invalidate specific user's preferences
 * - Use profileQueryKeys.all to invalidate all profile-related data
 *
 * Usage Examples:
 * ```typescript
 * // Basic usage with useCurrentUser
 * const { id } = useCurrentUser();
 * const { data: preferences, isLoading } = useAccessibilityPreferencesQuery({
 *   userId: id!,
 * });
 *
 * // With custom configuration
 * const { data } = useAccessibilityPreferencesQuery({
 *   userId: id!,
 *   queryConfig: { staleTime: 1000 * 60 * 10 }, // 10 minutes
 * });
 *
 * // Using query options for prefetching
 * await queryClient.prefetchQuery(getAccessibilityPreferencesQueryOptions(userId));
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { QueryConfig } from '@/lib/react-query';
import { profileQueryKeys } from './query-keys';
import {
  AccessibilityPreferencesRow,
  DEFAULT_ACCESSIBILITY_PREFERENCES,
} from './types';

/**
 * AccessibilityPreferences type
 * Represents the preference fields without system columns (id, user_id, timestamps)
 * This is the return type for the API layer
 */
export type AccessibilityPreferences = Omit<
  AccessibilityPreferencesRow,
  'id' | 'user_id' | 'created_at' | 'updated_at'
>;

/**
 * Layer 1: Pure fetcher function
 * Fetches accessibility preferences from the database.
 *
 * Behavior:
 * - Returns preferences from database if record exists
 * - Returns DEFAULT_ACCESSIBILITY_PREFERENCES if no record exists
 * - Throws on database errors (except PGRST116 which indicates no rows)
 *
 * @param userId - User UUID from auth.users
 * @returns AccessibilityPreferences object with all preference values
 * @throws Error if database query fails (non-404 errors)
 */
export const getAccessibilityPreferences = async (
  userId: string
): Promise<AccessibilityPreferences> => {
  const { data, error } = await supabase
    .from('user_preferences_accessibility')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  // Handle database errors (PGRST116 = no rows found, which is expected)
  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching accessibility preferences:', error);
    throw error;
  }

  // Return defaults if no record exists for this user
  if (!data) {
    return DEFAULT_ACCESSIBILITY_PREFERENCES;
  }

  // Return preferences with null coalescing to defaults for safety
  return {
    dyslexia_friendly_font: data.dyslexia_friendly_font ?? false,
    high_contrast_mode: data.high_contrast_mode ?? false,
    large_text_mode: data.large_text_mode ?? false,
    reduced_motion: data.reduced_motion ?? false,
  };
};

/**
 * Layer 2: Query options factory
 * Provides configuration for React Query with type-safe query keys.
 *
 * @param userId - User UUID from auth.users
 * @returns Query options object for use with useQuery or prefetchQuery
 */
export const getAccessibilityPreferencesQueryOptions = (userId: string) => ({
  queryKey: profileQueryKeys.accessibility(userId),
  queryFn: () => getAccessibilityPreferences(userId),
  staleTime: 1000 * 60 * 5, // 5 minutes
  enabled: !!userId,
});

/**
 * Hook options interface for useAccessibilityPreferencesQuery
 */
export interface UseAccessibilityPreferencesQueryOptions {
  /** User UUID from auth.users */
  userId: string;
  /** Optional query configuration overrides */
  queryConfig?: QueryConfig<typeof getAccessibilityPreferencesQueryOptions>;
}

/**
 * Layer 3: React Query hook
 * Provides data, loading state, and error handling for accessibility preferences.
 *
 * NOTE: This hook is named useAccessibilityPreferencesQuery (not useAccessibilityPreferences)
 * to avoid conflict with the existing wrapper hook that handles CSS application
 * and debounced updates.
 *
 * @param options - Hook options including userId and optional queryConfig
 * @returns React Query result with AccessibilityPreferences
 *
 * @example
 * ```typescript
 * const { data: preferences, isLoading, error } = useAccessibilityPreferencesQuery({
 *   userId: currentUserId,
 * });
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 *
 * return <PreferencesForm preferences={preferences} />;
 * ```
 */
export const useAccessibilityPreferencesQuery = ({
  userId,
  queryConfig,
}: UseAccessibilityPreferencesQueryOptions) => {
  return useQuery({
    ...getAccessibilityPreferencesQueryOptions(userId),
    ...queryConfig,
  });
};
